import { useEffect } from "react";
import { useGetActiveRaffleQuery, useGetRaffleByIdQuery } from "../api";
import { saveActiveRaffleAction } from "../slices/raffle";
import { RootState } from "../types/rootState";
import { useAppDispatch } from "./useAppDispatch";
import { useAppSelector } from "./useAppSelector";

/**
 * Resuelve la rifa que debe mostrar una página pública.
 * - Con raffleId: la busca por id (solo si pertenece a la cuenta actual).
 * - Sin raffleId en cuenta normal: la rifa activa de la cuenta (comportamiento histórico).
 * - Sin raffleId en cuenta multi-rifa: no consulta y pide al usuario elegir una (needsSelection).
 */
export const usePublicRaffle = (raffleId?: string | null) => {
  const dispatch = useAppDispatch();

  const accountId = useAppSelector(({ account }: RootState) => account.id);
  const isMultiRaffle = useAppSelector(({ account }: RootState) => !!account.isMultiRaffle);
  const isAccountLoaded = accountId !== "default" && accountId !== "";

  const byIdQuery = useGetRaffleByIdQuery(raffleId ?? "", {
    skip: !raffleId,
  });

  const activeQuery = useGetActiveRaffleQuery(accountId, {
    skip: !!raffleId || !isAccountLoaded || isMultiRaffle,
  });

  const needsSelection = !raffleId && isAccountLoaded && isMultiRaffle;
  const query = raffleId ? byIdQuery : activeQuery;

  const raffle = isAccountLoaded && query.data?.accountId === accountId ? query.data : undefined;

  useEffect(() => {
    if (raffle) {
      dispatch(saveActiveRaffleAction(raffle));
    }
  }, [raffle, dispatch]);

  return {
    raffle,
    needsSelection,
    isLoading: query.isLoading || (!!raffleId && !isAccountLoaded),
    isUninitialized: needsSelection ? false : query.isUninitialized,
    error: query.error,
  };
};
